import styled, { css } from 'styled-components';
import type { ColorName } from 'styles/config/colors';
import themeColors, { baseColors } from 'styles/config/colors';
import themeSpacing from 'styles/config/spacing';
import text from 'styles/config/text';
import transition from 'styles/config/transition';

import { BodyStrong } from '../LegacyText';
import UnstyledLoadingIcon from '../LoadingIcon';
import type { SpacingStyledProps } from '../Spacing';
import { SpacingStyles } from '../Spacing';
import type { ButtonLabelSize, ButtonVariant } from '.';

export const variantToButtonIconColor: {
  [key in keyof typeof ButtonVariant]: ColorName;
} = {
  BRAND: 'iconButtonBrand',
  PRIMARY: 'iconPrimaryInverse',
  SECONDARY: 'iconSecondary',
  GHOST: 'iconPrimary',
  SUCCESS: 'iconPrimaryInverse',
  DANGER: 'iconPrimaryInverse',
  MAKE_IT_POP: 'iconPrimaryInverse',
  LINK: 'iconSecondary',
};

export const variantToInverseButtonIconColor: {
  [key in keyof typeof ButtonVariant]: ColorName;
} = {
  BRAND: 'iconButtonBrand',
  PRIMARY: 'iconButtonBrand',
  SECONDARY: 'iconPrimaryInverse',
  GHOST: 'iconPrimaryInverse',
  SUCCESS: 'iconPrimaryInverse',
  DANGER: 'iconPrimaryInverse',
  MAKE_IT_POP: 'iconPrimaryInverse',
  LINK: 'iconSecondaryInverse',
};

const BrandStyles = css`
  color: ${themeColors.textButtonBrand};
  background: ${themeColors.backgroundBrand};

  &:hover {
    background: ${themeColors.backgroundBrandHover};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }
`;

const BrandInverseStyles = css`
  ${BrandStyles}

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }
`;

const PrimaryStyles = css`
  color: ${themeColors.buttonPrimaryText};
  background: ${themeColors.buttonPrimaryBackground};

  &:hover {
    background: ${themeColors.buttonPrimaryBackgroundHover};
  }

  &:focus {
    color: ${themeColors.buttonPrimaryText};
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }
`;

const PrimaryInverseStyles = css`
  ${PrimaryStyles}
  color: ${themeColors.textButtonBrand};
  background: ${themeColors.backgroundPrimaryInverse};

  &:hover {
    background: ${themeColors.backgroundPrimaryInverseHover};
  }

  &:focus {
    color: ${themeColors.textButtonBrand};
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }
`;

const SecondaryStyles = css`
  color: ${themeColors.textPrimary};
  background: transparent !important;
  box-shadow: inset 0 0 0 1px ${themeColors.borderSubtle};

  &:hover {
    box-shadow: inset 0 0 0 1px ${themeColors.borderStrong};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }

  &:disabled {
    &,
    &:hover,
    &:active,
    &:focus {
      background: transparent !important;
      box-shadow: inset 0 0 0 1px ${themeColors.borderSubtle};
    }
  }
`;

const SecondaryInverseStyles = css`
  ${SecondaryStyles}
  color: ${themeColors.textPrimaryInverse} !important;
  box-shadow: inset 0 0 0 1px ${themeColors.borderSubtleInverse};

  &:hover {
    background: ${themeColors.backgroundInverseHover} !important;
    box-shadow: inset 0 0 0 1px ${themeColors.borderStrongInverse};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }

  &:disabled {
    &,
    &:hover,
    &:active,
    &:focus {
      box-shadow: inset 0 0 0 1px ${themeColors.borderStrongInverse};
    }
  }
`;

const GhostStyles = css`
  color: ${themeColors.textPrimary};
  background: transparent !important;
  border: none;

  &:hover {
    background: ${themeColors.backgroundHover} !important;
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }
`;

const GhostInverseStyles = css`
  ${GhostStyles}
  color: ${themeColors.textPrimaryInverse} !important;

  &:hover {
    background: ${themeColors.backgroundInverseHover};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }

  &:disabled {
    &,
    &:hover,
    &:active,
    &:focus {
      background: transparent !important;
    }
  }
`;

const SuccessStyles = css`
  color: ${themeColors.textPrimaryInverse} !important;
  background: ${themeColors.backgroundSuccess};

  &:hover {
    background: ${themeColors.backgroundSuccessHover};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }
`;

const SuccessInverseStyles = css`
  ${SuccessStyles};
  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }
`;

const DangerStyles = css`
  color: ${themeColors.textPrimaryInverse} !important;
  background: ${themeColors.backgroundDanger};

  &:hover {
    background: ${themeColors.backgroundDangerHover};
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderFocus};
  }
`;

const DangerInverseStyles = css`
  ${DangerStyles};
  &:focus {
    box-shadow: inset 0 0 0 2px ${themeColors.borderInverseFocus};
  }
`;

{
  /* 🪦 faiz: 2022-2024 */
}
const MakeItPopStyles = css`
  @property --gradient-angle {
    inherits: false;
    initial-value: 0deg;
    syntax: "<angle>";
  }

  border: none;
  outline: none;
  transition: all 0.5s cubic-bezier(0.2, 2.3, 0.4, 1.2);

  &::before {
    content: "";

    position: absolute;
    z-index: -1;
    inset: -4px;

    background: conic-gradient(
      from var(--gradient-angle),
      ${themeColors.borderBrand},
      ${themeColors.borderPrimary},
      transparent,
      ${themeColors.borderPrimary},
      ${themeColors.borderBrand}
    );
    filter: blur(0.05rem);
    border-radius: 16px;

    transition: all 0.2s ease-in-out;
    animation: rotation 2s forwards infinite;
  }

  @keyframes rotation {
    0% {
      --gradient-angle: 0deg;
    }

    100% {
      --gradient-angle: 360deg;
    }
  }

  &::after {
    content: "";

    position: absolute;
    z-index: -1;
    inset: 0;

    background: ${themeColors.backgroundPrimary};
    filter: blur(0.1rem);
    border-radius: 12px;
  }

  &:hover {
    scale: 1.05;
    color: black;
    background: ${themeColors.backgroundHover};

    &::before {
      inset: -3px;
      filter: blur(0.5rem);
    }

    &::after {
      background: ${themeColors.backgroundHover};
    }
  }
`;

const LinkStyles = css`
  padding: 0;

  color: ${themeColors.textSecondary};

  background: transparent !important;
  border: none;
  border-radius: 0;

  &:not(:disabled) {
    &:hover,
    &:focus {
      border-bottom: 1px solid ${themeColors.textSecondary};
    }
  }
`;

const LinkInverseStyles = css`
  padding: 0;

  color: ${themeColors.textSecondaryInverse};

  background: transparent !important;
  border: none;
  border-radius: 0;

  &:not(:disabled) {
    &:hover,
    &:focus {
      border-bottom: 1px solid ${themeColors.textSecondary};
    }
  }
`;

interface ButtonProps extends SpacingStyledProps {
  $variant?: keyof typeof ButtonVariant;
  $inverse?: boolean;
  $size?: ButtonLabelSize;
  $loading?: boolean;
  $fullWidth?: boolean;
  $hasAdornment?: boolean;
  $disabled?: boolean;
  $show?: boolean;
  $color?: ColorName;
  $backgroundColor?: ColorName;
}

export const ButtonWrapper = styled.button<ButtonProps>`
  position: relative;

  padding: ${themeSpacing.xs2} ${themeSpacing.sm2};

  color: ${themeColors.textPrimaryInverse};
  text-align: center;

  background: ${themeColors.backgroundPrimary};
  border-radius: ${themeSpacing.sm2};

  transition: all 0.3s ${transition.easeSineOut};

  ${({ $size }) =>
    $size === 'small' &&
    css`
      padding: ${themeSpacing.xs2} ${themeSpacing.sm2};
    `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `} 

  &:disabled {
    cursor: not-allowed;

    &,
    &:active,
    &:focus {
      color: ${({ $inverse }) =>
        $inverse ? themeColors.textDisabledInverse : themeColors.textDisabled};
      background: ${({ $inverse }) =>
        $inverse
          ? themeColors.backgroundAccentInverse
          : themeColors.backgroundDisabledSubtle};
    }

    &:hover {
      background: ${({ $inverse }) =>
        $inverse
          ? themeColors.backgroundAccentInverse
          : themeColors.backgroundDisabledSubtle} !important;
    }
  }

  ${({ $variant, $inverse }) => {
    switch ($variant) {
      case 'BRAND':
        return $inverse ? BrandInverseStyles : BrandStyles;
      case 'PRIMARY':
        return $inverse ? PrimaryInverseStyles : PrimaryStyles;
      case 'SECONDARY':
        return $inverse ? SecondaryInverseStyles : SecondaryStyles;
      case 'GHOST':
        return $inverse ? GhostInverseStyles : GhostStyles;
      case 'SUCCESS':
        return $inverse ? SuccessInverseStyles : SuccessStyles;
      case 'DANGER':
        return $inverse ? DangerInverseStyles : DangerStyles;
      case 'MAKE_IT_POP':
        return MakeItPopStyles;
      case 'LINK':
        return $inverse ? LinkInverseStyles : LinkStyles;
      default:
        return PrimaryStyles;
    }
  }}

  ${SpacingStyles}

  ${({ $color }) =>
    $color &&
    css`
      color: ${themeColors[$color]};
    `}

  ${({ $backgroundColor }) =>
    $backgroundColor &&
    css`
      background: ${themeColors[$backgroundColor]} !important;
    `}
`;

export const IconOnlyButtonWrapper = styled(ButtonWrapper)<SpacingStyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;
  padding: ${themeSpacing.iconButton};

  &:disabled {
    cursor: not-allowed;

    &,
    &:active,
    &:focus {
      background: ${({ $inverse }) =>
        $inverse
          ? themeColors.backgroundAccentInverse
          : themeColors.backgroundDisabledSubtle};
    }

    &:hover {
      background: ${({ $inverse }) =>
        $inverse
          ? themeColors.backgroundAccentInverse
          : themeColors.backgroundDisabledSubtle} !important;
    }
  }

  ${({ $size }) =>
    $size === 'small' &&
    css`
      width: 32px;
      height: 32px;
      padding: 6px;
    `}

  ${SpacingStyles}
`;

export const ButtonLabel = styled(props => (
  <BodyStrong {...props} as="span" />
))<ButtonProps>`
  position: relative;
  top: 50%;
  transform: translateY(-65%);
  min-width: fit-content;

  ${({ $size }) =>
    $size === 'small' &&
    css`
      font-size: ${text.size.body.small};
    `}

  ${({ $hasAdornment }) =>
    $hasAdornment &&
    css`
      margin: 0 ${themeSpacing.xs1};
    `}
  
  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${themeColors.textDisabled};
    `}
`;

export const ButtonContentContainer = styled.span<ButtonProps>`
  display: flex;
  flex-direction: row;
  gap: ${themeSpacing.xs1};
  align-items: center;
  justify-content: center;

  height: ${themeSpacing.sm3};

  opacity: ${({ $show }) => ($show ? 1 : 0)};

  transition: opacity 0.1s ${transition.easeSineOut};

  ${({ $size }) =>
    $size === 'small' &&
    css`
      height: 20px;
    `}
`;

export const LoadingIcon = styled(UnstyledLoadingIcon)<{ $show: boolean }>`
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);

  opacity: ${({ $show }) => ($show ? 1 : 0)};

  transition: opacity 0.1s ${transition.easeSineOut};
`;

export const Badge = styled.span<{ $color?: ColorName }>`
  position: absolute;
  top: ${themeSpacing.xxs2};
  right: ${themeSpacing.xxs2};

  width: ${themeSpacing.xs2};
  height: ${themeSpacing.xs2};

  background-color: ${({ $color }) => ($color ? themeColors[$color] : baseColors.red[50])};
  border-radius: 50%;
`;
